import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { API } from "../config/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Search, ExternalLink, Loader2, FileText, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";


export default function PortalJustPage() {
  const { getAuthHeader } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);

  useEffect(() => {
    // Load recent searches from localStorage
    const saved = localStorage.getItem("portalJustSearches");
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  const saveSearch = (query) => {
    const updated = [query, ...recentSearches.filter((s) => s !== query)].slice(0, 10);
    setRecentSearches(updated);
    localStorage.setItem("portalJustSearches", JSON.stringify(updated));
  };

  const handleSearch = async (query = searchQuery) => {
    if (!query.trim()) {
      toast.error("Introduceți numărul dosarului sau numele unei părți");
      return;
    }

    setSearching(true);
    setResults(null);

    try {
      const response = await axios.post(
        `${API}/portal-just/search`,
        { numar_dosar: query },
        getAuthHeader()
      );
      setResults(response.data);
      saveSearch(query);
    } catch (error) {
      toast.error("Eroare la căutare");
      setResults({
        success: false,
        error: error.message,
        search_url: "https://portal.just.ro/SitePages/cautare.aspx",
      });
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="portal-just-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Portal Just.ro</h1>
        <p className="font-body text-muted-foreground mt-1">
          Căutare dosare pe portal.just.ro
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Search Section */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Search className="w-5 h-5" />
                Căutare Dosar
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-1">
                  <Input
                    placeholder="Ex: 1234/123/2024 sau Nume Parte"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    data-testid="search-input"
                  />
                </div>
                <Button onClick={() => handleSearch()} disabled={searching} data-testid="search-btn">
                  {searching ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  <span className="ml-2">Caută</span>
                </Button>
              </div>

              {/* Results */}
              {results && (
                <div className="mt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading text-lg">Rezultate pentru: {results.numar_dosar}</h3>
                    <Button variant="outline" size="sm" asChild>
                      <a
                        href={results.search_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="open-portal-link"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Deschide Portal
                      </a>
                    </Button>
                  </div>

                  {results.success ? (
                    results.results?.length > 0 ? (
                      <div 
                        className="rounded-md border overflow-hidden"
                        style={{ maxHeight: results.results.length > 5 ? '580px' : 'auto' }}
                      >
                        <div 
                          className={`p-4 space-y-3 ${results.results.length > 5 ? 'overflow-y-auto' : ''}`}
                          style={{ maxHeight: results.results.length > 5 ? '572px' : 'auto' }}
                        >
                          {results.results.map((result, index) => (
                            <div key={index} className={`p-4 rounded-lg border hover:border-primary/50 transition-colors ${
                              result.source === "local" ? "bg-blue-50/50 border-blue-200" : "bg-muted"
                            }`}>
                              {result.source === "local" && (
                                <Badge variant="outline" className="text-[10px] mb-2 text-blue-600 border-blue-300">
                                  Din baza de date CRM
                                </Badge>
                              )}
                              {/* Linia 1: Număr dosar cu link */}
                              {result.url ? (
                                <a
                                  href={result.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-mono text-base font-semibold text-primary hover:underline flex items-center gap-2"
                                >
                                  {result.numar_dosar}
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              ) : (
                                <span className="font-mono text-base font-semibold">{result.numar_dosar}</span>
                              )}
                              
                              {/* Părți (pentru rezultate locale) */}
                              {result.parti?.length > 0 && (
                                <div className="mt-1 text-sm">
                                  <span className="text-muted-foreground">Părți: </span>
                                  <span className="font-medium">
                                    {result.parti.map(p => p.nume).join(", ")}
                                  </span>
                                </div>
                              )}
                              
                              {/* Liniile 2, 3, 4: Instanță, Obiect, Stadiu */}
                              <div className="mt-2 space-y-1 text-sm bg-background/50 rounded p-2">
                                {result.instanta && (
                                  <div className="flex items-start gap-2">
                                    <span className="text-muted-foreground font-medium min-w-[70px]">Instanță:</span>
                                    <span className="font-body">{result.instanta}</span>
                                  </div>
                                )}
                                {result.obiect && (
                                  <div className="flex items-start gap-2">
                                    <span className="text-muted-foreground font-medium min-w-[70px]">Obiect:</span>
                                    <span className="font-body">{result.obiect}</span>
                                  </div>
                                )}
                                {result.stadiu_procesual && (
                                  <div className="flex items-start gap-2">
                                    <span className="text-muted-foreground font-medium min-w-[70px]">Stadiu:</span>
                                    <span className="font-body">{result.stadiu_procesual}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="p-6 text-center border rounded-md">
                        <AlertCircle className="w-10 h-10 text-warning mx-auto mb-3" />
                        <p className="font-body text-muted-foreground">
                          Nu s-au găsit rezultate directe. Accesați portal.just.ro pentru căutare manuală.
                        </p>
                      </div>
                    )
                  ) : (
                    <div className="p-6 text-center border rounded-md border-destructive/50">
                      <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-3" />
                      <p className="font-body text-destructive">{results.error || results.message}</p>
                      <p className="font-body text-sm text-muted-foreground mt-2">
                        Accesați link-ul direct pentru căutare manuală.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Info Box */}
              <div className="p-4 bg-info/10 rounded-md border border-info/20">
                <p className="font-body text-sm text-info">
                  <strong>Notă:</strong> Puteți căuta după număr dosar (ex: 1234/123/2024) sau după numele unei părți. 
                  Căutarea ignoră diacriticele (ă, ș, ț). Pentru rezultate complete, recomandăm accesarea directă a portalului.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Searches */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Căutări Recente</CardTitle>
            </CardHeader>
            <CardContent>
              {recentSearches.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground font-body text-sm">
                  Nu există căutări recente.
                </p>
              ) : (
                <div className="space-y-2">
                  {recentSearches.map((query, index) => (
                    <Button
                      key={index}
                      variant="ghost"
                      className="w-full justify-start font-mono text-sm"
                      onClick={() => {
                        setSearchQuery(query);
                        handleSearch(query);
                      }}
                    >
                      <FileText className="w-4 h-4 mr-2 text-muted-foreground" />
                      {query}
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Links */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Link-uri Utile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full justify-start" asChild>
                <a href="https://portal.just.ro" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Portal Just.ro
                </a>
              </Button>
              <Button variant="outline" className="w-full justify-start" asChild>
                <a href="https://portal.just.ro/SitePages/cautare.aspx" target="_blank" rel="noopener noreferrer">
                  <Search className="w-4 h-4 mr-2" />
                  Căutare Dosare
                </a>
              </Button>
              <Button variant="outline" className="w-full justify-start" asChild>
                <a href="https://portal.just.ro/SitePages/sedinte.aspx" target="_blank" rel="noopener noreferrer">
                  <FileText className="w-4 h-4 mr-2" />
                  Ședințe de Judecată
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
